import type { ColumnTemplate } from '@thinkalpha/ui-types/contracts/column-template';
import type { LocatesV2FormData, QuickSendButton } from '@thinkalpha/ui-types/contracts/workspace';
import type { LocatesV2Sort } from 'src/queries/locatesV2/sorts';

export type UpdateLocatesV2SymbolAction = { type: 'updateLocatesV2Symbol'; tabId: string; symbol: string };
export const updateLocatesV2Symbol = (tabId: string, symbol: string): UpdateLocatesV2SymbolAction => ({
    tabId,
    symbol,
    type: 'updateLocatesV2Symbol',
});

export type UserSelectedNewLocatesV2SymbolAction = {
    type: 'userSelectedNewLocatesV2Symbol';
    symbol: string;
    tabId: string;
};

export const userSelectedNewLocatesV2Symbol = (
    tabId: string,
    symbol: string,
): UserSelectedNewLocatesV2SymbolAction => ({
    symbol,
    tabId,
    type: 'userSelectedNewLocatesV2Symbol',
});

export type UpdateLocatesV2AccountAction = {
    type: 'updateLocatesV2Account';
    tabId: string;
    accountId: string | null;
};
export const updateLocatesV2Account = (tabId: string, accountId: string): UpdateLocatesV2AccountAction => ({
    tabId,
    accountId,
    type: 'updateLocatesV2Account',
});

export type SetLocatesV2DisplayingQuoteAction = {
    type: 'setLocatesV2DisplayingQuote';
    tabId: string;
    isShowing: boolean;
};

export const setLocatesV2DisplayingQuote = (tabId: string, isShowing: boolean): SetLocatesV2DisplayingQuoteAction => ({
    tabId,
    isShowing,
    type: 'setLocatesV2DisplayingQuote',
});

export type SetLocatesV2DisplayingResultsAction = {
    type: 'setLocatesV2DisplayingResults';
    tabId: string;
    isShowing: boolean;
};

export const setLocatesV2DisplayingResults = (
    tabId: string,
    isShowing: boolean,
): SetLocatesV2DisplayingResultsAction => ({
    tabId,
    isShowing,
    type: 'setLocatesV2DisplayingResults',
});

export type SetLocatesV2DisplayingCostInfoAction = {
    type: 'setLocatesV2DisplayingCostInfo';
    tabId: string;
    isShowing: boolean;
};

export const setLocatesV2DisplayingCostInfo = (
    tabId: string,
    isShowing: boolean,
): SetLocatesV2DisplayingCostInfoAction => ({
    tabId,
    isShowing,
    type: 'setLocatesV2DisplayingCostInfo',
});

export type StoreLocatesV2SplitPercentageAction = {
    type: 'storeLocatesV2SplitPercentage';
    tabId: string;
    splitPercentage: number;
};

export const storeLocatesV2SplitPercentage = (
    tabId: string,
    splitPercentage: number,
): StoreLocatesV2SplitPercentageAction => ({
    tabId,
    splitPercentage,
    type: 'storeLocatesV2SplitPercentage',
});

export type UpdateLocatesV2FormDataAction = {
    type: 'updateLocatesV2FormData';
    tabId: string;
    formData: LocatesV2FormData;
};

export const updateLocatesV2FormData = (tabId: string, formData: LocatesV2FormData): UpdateLocatesV2FormDataAction => ({
    tabId,
    formData,
    type: 'updateLocatesV2FormData',
});

export type UpdateLocatesV2QuickSendButtonsAction = {
    type: 'updateLocatesV2QuickSendButtons';
    tabId: string;
    buttons: QuickSendButton[];
};

export const updateLocatesV2QuickSendButtons = (
    tabId: string,
    buttons: QuickSendButton[],
): UpdateLocatesV2QuickSendButtonsAction => ({
    tabId,
    buttons,
    type: 'updateLocatesV2QuickSendButtons',
});

export type SetLocatesV2SoundAlertAction = {
    type: 'setLocatesV2SoundAlert';
    tabId: string;
    enabled: boolean;
};

export const setLocatesV2SoundAlert = (tabId: string, enabled: boolean): SetLocatesV2SoundAlertAction => ({
    tabId,
    enabled,
    type: 'setLocatesV2SoundAlert',
});

export type SetLocatesV2QuantityKiloAction = {
    type: 'setLocatesV2QuantityKilo';
    tabId: string;
    enabled: boolean;
};

export const setLocatesV2QuantityKilo = (tabId: string, enabled: boolean): SetLocatesV2QuantityKiloAction => ({
    tabId,
    enabled,
    type: 'setLocatesV2QuantityKilo',
});

export type SetLocatesV2UserOnlyAction = {
    type: 'setLocatesV2UserOnly';
    tabId: string;
    enabled: boolean;
};

export const setLocatesV2UserOnly = (tabId: string, enabled: boolean): SetLocatesV2UserOnlyAction => ({
    tabId,
    enabled,
    type: 'setLocatesV2UserOnly',
});

export type SetLocatesV2OnlyPendingAction = {
    type: 'setLocatesV2OnlyPending';
    tabId: string;
    enabled: boolean;
};

export const setLocatesV2OnlyPending = (tabId: string, enabled: boolean): SetLocatesV2OnlyPendingAction => ({
    tabId,
    enabled,
    type: 'setLocatesV2OnlyPending',
});

export type SetLocatesV2OnlyResultsAction = {
    type: 'setLocatesV2OnlyResults';
    tabId: string;
    enabled: boolean;
};

export const setLocatesV2OnlyResults = (tabId: string, enabled: boolean): SetLocatesV2OnlyResultsAction => ({
    tabId,
    enabled,
    type: 'setLocatesV2OnlyResults',
});

export type SetLocatesV2OnlyUserActivityAction = {
    type: 'setLocatesV2OnlyUserActivity';
    tabId: string;
    enabled: boolean;
};

export const setLocatesV2OnlyUserActivity = (tabId: string, enabled: boolean): SetLocatesV2OnlyUserActivityAction => ({
    tabId,
    enabled,
    type: 'setLocatesV2OnlyUserActivity',
});

export type SetLocatesV2AccountIdsAction = {
    type: 'setLocatesV2AccountIds';
    tabId: string;
    accountIds: string[] | null;
};

export const setLocatesV2AccountIds = (tabId: string, accountIds: string[] | null): SetLocatesV2AccountIdsAction => ({
    tabId,
    accountIds,
    type: 'setLocatesV2AccountIds',
});

export type UpdateLocatesV2CostInfoColumnTemplateAction = {
    type: 'updateLocatesV2CostInfoColumnTemplate';
    tabId: string;
    columnTemplate: ColumnTemplate | string | null;
};

export const updateLocatesV2CostInfoColumnTemplate = (
    tabId: string,
    columnTemplate: ColumnTemplate | string | null,
): UpdateLocatesV2CostInfoColumnTemplateAction => ({
    tabId,
    columnTemplate,
    type: 'updateLocatesV2CostInfoColumnTemplate',
});

export type UpdateLocatesV2FilterAction = {
    type: 'updateLocatesV2Filter';
    tabId: string;
    filter: string | null;
};

export const updateLocatesV2Filter = (tabId: string, filter: string | null): UpdateLocatesV2FilterAction => ({
    tabId,
    filter,
    type: 'updateLocatesV2Filter',
});

export type UpdateLocatesV2ColumnWidthsAction = {
    type: 'updateLocatesV2ColumnWidths';
    tabId: string;
    columnWidths: Record<string, number>;
};

export const updateLocatesV2ColumnWidths = (
    tabId: string,
    columnWidths: Record<string, number>,
): UpdateLocatesV2ColumnWidthsAction => ({
    tabId,
    columnWidths,
    type: 'updateLocatesV2ColumnWidths',
});

export type UpdateLocatesV2ColumnOrderAction = {
    type: 'updateLocatesV2ColumnOrder';
    tabId: string;
    columnOrder: string[];
};

export const updateLocatesV2ColumnOrder = (tabId: string, columnOrder: string[]): UpdateLocatesV2ColumnOrderAction => ({
    tabId,
    columnOrder,
    type: 'updateLocatesV2ColumnOrder',
});

export type UpdateLocatesV2ColumnVisibilityAction = {
    type: 'updateLocatesV2ColumnVisibility';
    tabId: string;
    columnVisibility: Record<string, boolean>;
};

export const updateLocatesV2ColumnVisibility = (
    tabId: string,
    columnVisibility: Record<string, boolean>,
): UpdateLocatesV2ColumnVisibilityAction => ({
    tabId,
    columnVisibility,
    type: 'updateLocatesV2ColumnVisibility',
});

export type UpdateLocatesV2SortAction = {
    type: 'updateLocatesV2Sort';
    tabId: string;
    sort: LocatesV2Sort;
};

export const updateLocatesV2Sort = (tabId: string, sort: LocatesV2Sort): UpdateLocatesV2SortAction => ({
    tabId,
    sort,
    type: 'updateLocatesV2Sort',
});

export type SetLocatesV2AutoAcceptTypeAction = {
    type: 'setLocatesV2AutoAcceptType';
    tabId: string;
    autoAcceptType: 'price' | 'percent';
};

export const setLocatesV2AutoAcceptType = (
    tabId: string,
    autoAcceptType: 'price' | 'percent',
): SetLocatesV2AutoAcceptTypeAction => ({
    tabId,
    autoAcceptType,
    type: 'setLocatesV2AutoAcceptType',
});

export type SetLocatesV2AutoAcceptPercentAction = {
    type: 'setLocatesV2AutoAcceptPercent';
    tabId: string;
    autoAcceptPercent: number;
};

export const setLocatesV2AutoAcceptPercent = (
    tabId: string,
    autoAcceptPercent: number,
): SetLocatesV2AutoAcceptPercentAction => ({
    tabId,
    autoAcceptPercent,
    type: 'setLocatesV2AutoAcceptPercent',
});

export type LocatesV2Actions =
    | UserSelectedNewLocatesV2SymbolAction
    | UpdateLocatesV2SymbolAction
    | UpdateLocatesV2AccountAction
    | SetLocatesV2DisplayingQuoteAction
    | SetLocatesV2DisplayingResultsAction
    | SetLocatesV2DisplayingCostInfoAction
    | StoreLocatesV2SplitPercentageAction
    | UpdateLocatesV2FormDataAction
    | UpdateLocatesV2QuickSendButtonsAction
    | SetLocatesV2SoundAlertAction
    | SetLocatesV2QuantityKiloAction
    | SetLocatesV2UserOnlyAction
    | SetLocatesV2OnlyPendingAction
    | SetLocatesV2OnlyResultsAction
    | SetLocatesV2OnlyUserActivityAction
    | SetLocatesV2AccountIdsAction
    | UpdateLocatesV2CostInfoColumnTemplateAction
    | UpdateLocatesV2FilterAction
    | UpdateLocatesV2ColumnWidthsAction
    | UpdateLocatesV2ColumnOrderAction
    | UpdateLocatesV2ColumnVisibilityAction
    | UpdateLocatesV2SortAction
    | SetLocatesV2AutoAcceptTypeAction
    | SetLocatesV2AutoAcceptPercentAction;
